import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { useToolSeo, buildToolJsonLd } from "@/components/tools/seo";
import ToolFaq from "@/components/tools/ToolFaq";
import ToolInternalLinksSidebar from "@/components/tools/ToolInternalLinksSidebar";
import ToolFeaturedArticles from "@/components/tools/ToolFeaturedArticles";

export default function ToolPageShell({ tool, intro, faqs, children }) {
    useToolSeo({
        title: tool.metaTitle,
        description: tool.metaDescription,
        canonical:
            typeof window !== "undefined"
                ? `${window.location.origin}/tools/${tool.slug}`
                : undefined,
        structuredData: buildToolJsonLd({ tool, faqs }),
    });

    return (
        <PublicLayout>
            {/* Breadcrumb + Hero */}
            <section
                className="bl-section pt-10 sm:pt-14 pb-6 bg-bl-bg"
                data-testid={`tool-page-${tool.slug}`}
            >
                <div className="bl-container max-w-5xl">
                    <nav
                        className="flex items-center gap-1.5 text-xs text-bl-muted mb-5"
                        aria-label="Breadcrumb"
                    >
                        <Link
                            to="/tools"
                            className="hover:text-bl underline-offset-4 hover:underline inline-flex items-center gap-1"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            All tools
                        </Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="truncate">{tool.name}</span>
                    </nav>

                    <h1
                        className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-bl leading-[1.1]"
                        data-testid="tool-h1"
                    >
                        {tool.h1}
                    </h1>
                    {intro && (
                        <p className="mt-5 text-base text-bl-muted leading-relaxed max-w-3xl">
                            {intro}
                        </p>
                    )}
                </div>
            </section>

            {/* Tool body + internal-links sidebar */}
            <section className="bl-section py-6 sm:py-8">
                <div className="bl-container max-w-5xl">
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_18rem] gap-8 lg:gap-10">
                        <div className="min-w-0">{children}</div>
                        <ToolInternalLinksSidebar tool={tool} />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <ToolFaq items={faqs} toolSlug={tool.slug} />

            {/* Featured guides (rich article cards) */}
            <ToolFeaturedArticles tool={tool} />
        </PublicLayout>
    );
}
